import type { IPolicy } from '@adornis/abac/core/types.js';
import { isNullish } from '@adornis/assert/isNullish.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import {
  ensureLoggedInPayload,
  type LoggedInEvaluationFunction,
} from './permissions/abac-payloads/checkLoggedInPayload.js';
import { MARTINUS_ABAC_PAYLOAD } from './permissions/abac-payloads/MartinusAbacPayload.js';
import { hasRoles } from './permissions/api/hasRoles.js';
import { Role } from './permissions/roles.js';
import type { UserOnUserPayload } from './users/UserPolicy.js';

/**
 * This policy should be empty and just hold the actions where i do not know where the should belong to.
 * Mainly used for quick and dirty acces control of beta things and stuff which is not ported to abac
 */

export enum MartinusTemporaryActions {
  VIEW_WIKI = 'VIEW_WIKI',
  VIEW_TEACHING_ASSISTANT = 'VIEW_TEACHING_ASSISTANT',

  VIEW_GROUPS = 'VIEW_GROUPS',
  GROUPS_TIMELINE = 'GROUPS_TIMELINE',

  VIEW_INTERVIEW_ADMINISTRATION = 'VIEW_INTERVIEW_ADMINISTRATION',
  VIEW_ACCOUNTING = 'VIEW_ACCOUNTING',
}

const MartinusTemporaryPolicyFunctions: Record<
  MartinusTemporaryActions,
  LoggedInEvaluationFunction<UserOnUserPayload>
> = {
  [MartinusTemporaryActions.VIEW_GROUPS]: async request => {
    const hasRequieredRoles = hasRoles({
      user: request.payload.currentUser,
      roles: [Role.BUCHHALTUNG, Role.SPENDEN_ADMIN, Role.PSB, Role.BUCHHALTUNG, Role.SUS_BEWERBER_BEARBEITUNG],
    });

    return {
      allowed: hasRequieredRoles,
      reason: hasRequieredRoles ? 'User has required roles' : 'Noone can see the groups',
    };
  },

  [MartinusTemporaryActions.GROUPS_TIMELINE]: async request => {
    if (hasRoles({ user: request.payload.currentUser, roles: [Role.PSB], contexts: [GLOBAL_CONTEXT] })) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Timeline sehen.',
      };
    }
    return {
      allowed: false,
      reason: 'Nutzer darf die Timeline sehen.',
    };
  },

  [MartinusTemporaryActions.VIEW_TEACHING_ASSISTANT]: async request => {
    const hasRequieredRoles = hasRoles({
      user: request.payload.currentUser,
      roles: [Role.BETA_TESTING_AI],
    });

    return {
      allowed: hasRequieredRoles,
      reason: hasRequieredRoles ? 'User has required roles' : 'Noone can see the groups',
    };
  },

  [MartinusTemporaryActions.VIEW_WIKI]: async request => {
    const hasRequieredRoles = hasRoles({
      user: request.payload.currentUser,
      roles: [Role.WIKI],
    });

    return {
      allowed: hasRequieredRoles,
      reason: hasRequieredRoles ? 'User has required roles' : 'Noone can see the groups',
    };
  },

  [MartinusTemporaryActions.VIEW_ACCOUNTING]: async request => {
    const hasRequieredRoles = hasRoles({
      user: request.payload.currentUser,
      roles: [Role.BUCHHALTUNG],
    });
    return {
      allowed: hasRequieredRoles,
      reason: hasRequieredRoles ? 'User is Buchhaltung' : 'Accounting is only available for Buchhaltung',
    };
  },

  [MartinusTemporaryActions.VIEW_INTERVIEW_ADMINISTRATION]: async request => {
    const hasRequieredRoles = hasRoles({
      user: request.payload.currentUser,
      roles: [Role.SUS_BEWERBER_BEARBEITUNG, Role.SCHULLEITER],
    });

    return {
      allowed: hasRequieredRoles,
      reason: hasRequieredRoles ? 'User has required roles' : 'Noone can see the groups',
    };
  },

  /**
   * A user can see himself and all other users which he has permission on for the school.
   * Dynamic roles are used to determine in which context a user lives
   */
  // [MartinusTemporaryActions.VIEW_PROFILE]: async request => {
  //   /**
  //    * Check if it is the user himself
  //    */
  //   if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
  //     return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
  //   }

  //   /**
  //    * Check if the currentUser is a parent of the viewed user
  //    */
  //   if (isParent(request.payload.currentUser, request.payload.viewedUser)) {
  //     return { allowed: true, reason: 'Nutzer kann die Daten seiner Kinder sehen.' };
  //   }

  //   /**
  //    * Check if the currentUser can see the student or sbs
  //    */
  //   const allowedRolesOnStudent = [
  //     Role.SPENDEN_ADMIN,
  //     Role.SCHULLEITER,
  //     Role.BUCHHALTUNG,
  //     Role.SEKRETAER,
  //     Role.SUS_BEWERBER_BEARBEITUNG,
  //   ];

  //   const hasCommonContextsStudents = await usersHaveSameSchoolOnRoles({
  //     currentUser: request.payload.currentUser,
  //     viewedUser: request.payload.viewedUser,
  //     rolesCurrentUser: allowedRolesOnStudent,
  //     rolesViewedUser: [...STUDENT_ROLES, ...SBS_ROLES, ...SPENDER_ROLES],
  //   });
  //   if (hasCommonContextsStudents) {
  //     return {
  //       allowed: true,
  //       reason: 'Nutzer darf den Schüler sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
  //     };
  //   }

  //   /**
  //    * Check if the currentUser can see the employee
  //    */
  //   const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB, Role.MAV];

  //   const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
  //     currentUser: request.payload.currentUser,
  //     viewedUser: request.payload.viewedUser,
  //     rolesCurrentUser: allowedRolesOnEmployee,
  //     rolesViewedUser: EMPLOYEE_ROLES,
  //   });
  //   if (hasCommonContextsEmployee) {
  //     return {
  //       allowed: true,
  //       reason: 'Nutzer darf den Mitarbeiter sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
  //     };
  //   }

  //   return {
  //     allowed: false,
  //     reason: 'Nutzer ist nicht an der gleichen Schule.',
  //   };
  // },
};

export const MartinusTemporaryPolicy: IPolicy<UserOnUserPayload> = {
  name: 'MartinusTemporaryPolicy',
  acceptingPayloadTypes: [MARTINUS_ABAC_PAYLOAD],
  async evaluate(request) {
    /**
     * Check logged in user
     */
    request.payload = ensureLoggedInPayload(request.payload);

    /**
     * Execute the correct function for the action
     */
    const result = await MartinusTemporaryPolicyFunctions[request.payload.action]?.(request);

    if (isNullish(result)) return false;
    return result;
  },
};
